import { useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { loadList, upsertList } from '../../lib/localList';
import type { Clawback, PayRun, ProofMethod, RunScope } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const FINANCE_ROLES = ['ASP_FINANCE_OFFICER'];
const PROOF_ROLES = ['ASP_FINANCE_OFFICER', 'DISTRICT_WELFARE_OFFICER', 'ASP_ADMIN'];

/**
 * S06 has no `GET /runs` list — pay runs built from this browser are cached
 * locally (each is the real response of build/approve/send). `GET /clawbacks`
 * is a genuine list endpoint and used directly.
 */
export function PayRuns() {
  const { persona } = useAuth();
  const isFinance = persona?.roles.includes('ASP_FINANCE_OFFICER') ?? false;
  const isCeoApprover = persona?.roles.includes('ASP_CEO_APPROVER') ?? false;
  const canProofOfLife = persona?.roles.some((r) => PROOF_ROLES.includes(r)) ?? false;
  const canSeeRuns = isFinance || isCeoApprover;

  const [runs, setRuns] = useState<PayRun[]>(() => loadList<PayRun>('payments.runs'));
  const [showBuild, setShowBuild] = useState(false);
  const [sendTarget, setSendTarget] = useState<PayRun | null>(null);
  const [showClawback, setShowClawback] = useState(false);
  const [recoverTarget, setRecoverTarget] = useState<Clawback | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [proofNin, setProofNin] = useState('');
  const [proofMethod, setProofMethod] = useState<ProofMethod>('DISTRICT_VISIT');

  const clawbacksQ = useApiQuery<{ items: Clawback[] }>(isFinance ? '/api/v1/payments/clawbacks' : null);

  if (!isFinance && !isCeoApprover) {
    return (
      <div>
        <PageHeader title="Pay Runs" />
        <ErrorNote message="This view requires the Finance Officer or CEO Approver role." />
      </div>
    );
  }

  const upsert = (r: PayRun) => setRuns(upsertList('payments.runs', r));

  const reviewExclusions = async (r: PayRun) => {
    setBusyId(r.id);
    setError(null);
    try {
      const updated = await api.post<PayRun>(`/api/v1/payments/runs/${r.id}/review-exclusions`);
      upsert({ ...r, ...updated });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  const approve = async (r: PayRun) => {
    setBusyId(r.id);
    setError(null);
    try {
      const updated = await api.post<PayRun>(`/api/v1/payments/runs/${r.id}/approve`);
      upsert({ ...r, ...updated });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Approval failed');
    } finally {
      setBusyId(null);
    }
  };

  const recordProofOfLife = async () => {
    setError(null);
    try {
      await api.post('/api/v1/payments/proof-of-life', { nin: proofNin, method: proofMethod });
      setError(`Proof of life recorded for ${proofNin}.`);
      setProofNin('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Benefit Payments & Reconciliation"
        subtitle="Monthly pay runs across active awards through the TSA, with the exclusion report reviewed before a rupee moves"
        actions={
          isFinance ? (
            <button className="btn-primary" onClick={() => setShowBuild(true)}>
              + Build run
            </button>
          ) : undefined
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      {canSeeRuns ? (
        <div className="mb-6 space-y-4">
          {runs.length === 0 ? (
            <div className="card">
              <EmptyState title="No pay runs yet" hint="Build one from the button above." />
            </div>
          ) : (
            runs.map((r) => (
              <div key={r.id} className="card p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-bold">
                      {r.period} · {r.scope}
                    </span>{' '}
                    <StatusBadge status={r.status} />
                    <div className="mt-0.5 text-xs text-slate-500">
                      {r.totals.included} included · {r.totals.excluded} excluded · total {scr(r.totals.amount)}
                      {r.batchRef ? ` · batch ${r.batchRef}` : ''}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-400">{r.approvals.map((a) => `${a.role} by ${a.by}`).join(' · ') || 'no approvals yet'}</div>
                  </div>
                  <div className="flex gap-2">
                    {isFinance && r.status === 'BUILT' ? (
                      <button className="btn-secondary" onClick={() => reviewExclusions(r)} disabled={busyId === r.id}>
                        Mark exclusions reviewed
                      </button>
                    ) : null}
                    {isFinance && r.status === 'EXCLUSIONS_REVIEWED' && r.approvals.length === 0 ? (
                      <button className="btn-success" onClick={() => approve(r)} disabled={busyId === r.id}>
                        Approve (Finance)
                      </button>
                    ) : null}
                    {isCeoApprover && r.status === 'EXCLUSIONS_REVIEWED' && r.approvals.length === 1 ? (
                      <button className="btn-primary" onClick={() => approve(r)} disabled={busyId === r.id}>
                        Approve (CEO)
                      </button>
                    ) : null}
                    {isFinance && r.status === 'DUAL_APPROVED' ? (
                      <button className="btn-primary" onClick={() => setSendTarget(r)}>
                        Send to Treasury
                      </button>
                    ) : null}
                  </div>
                </div>
                {Object.keys(r.exclusionReport.byReason).length > 0 ? (
                  <div className="mt-2 text-xs text-slate-600">
                    Exclusions:{' '}
                    {Object.entries(r.exclusionReport.byReason)
                      .map(([reason, n]) => `${reason} (${n})`)
                      .join(', ')}
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      ) : null}

      {canProofOfLife ? (
        <div className="card mb-6 p-4">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Record proof of life</h2>
          <div className="grid grid-cols-3 gap-2">
            <input className="field-input" placeholder="NIN" value={proofNin} onChange={(e) => setProofNin(e.target.value)} />
            <select className="field-input" value={proofMethod} onChange={(e) => setProofMethod(e.target.value as ProofMethod)}>
              <option value="ONEID_LOGIN">One Citizen login</option>
              <option value="DISTRICT_VISIT">District office visit</option>
              <option value="BIOMETRIC">Biometric check</option>
            </select>
            <button className="btn-secondary" onClick={recordProofOfLife} disabled={!proofNin}>
              Record
            </button>
          </div>
        </div>
      ) : null}

      {isFinance ? (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Clawbacks</h2>
            <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowClawback(true)}>
              + Open clawback
            </button>
          </div>
          <div className="card overflow-x-auto">
            {clawbacksQ.loading ? (
              <Spinner />
            ) : (clawbacksQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No clawbacks" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">NIN</th>
                    <th className="table-head">Reason</th>
                    <th className="table-head text-right">Amount</th>
                    <th className="table-head text-right">Recovered</th>
                    <th className="table-head">Status</th>
                    <th className="table-head" />
                  </tr>
                </thead>
                <tbody>
                  {clawbacksQ.data!.items.map((c) => (
                    <tr key={c.id}>
                      <td className="table-cell text-xs">{c.nin}</td>
                      <td className="table-cell text-xs">{c.reason}</td>
                      <td className="table-cell text-right tabular-nums">{scr(c.amount)}</td>
                      <td className="table-cell text-right tabular-nums">{scr(c.recoveredAmount)}</td>
                      <td className="table-cell">
                        <StatusBadge status={c.status} />
                      </td>
                      <td className="table-cell text-right">
                        {c.status !== 'CLOSED' ? (
                          <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setRecoverTarget(c)}>
                            Record recovery
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      {showBuild ? (
        <BuildRunModal
          onClose={() => setShowBuild(false)}
          onDone={(r) => {
            upsert(r);
            setShowBuild(false);
          }}
        />
      ) : null}
      {sendTarget ? (
        <SendRunModal
          run={sendTarget}
          onClose={() => setSendTarget(null)}
          onDone={(r) => {
            upsert({ ...sendTarget, ...r });
            setSendTarget(null);
          }}
        />
      ) : null}
      {showClawback ? (
        <ClawbackModal
          onClose={() => setShowClawback(false)}
          onDone={() => {
            setShowClawback(false);
            clawbacksQ.reload();
          }}
        />
      ) : null}
      {recoverTarget ? (
        <RecoverModal
          clawback={recoverTarget}
          onClose={() => setRecoverTarget(null)}
          onDone={() => {
            setRecoverTarget(null);
            clawbacksQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function BuildRunModal({ onClose, onDone }: { onClose: () => void; onDone: (r: PayRun) => void }) {
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [scope, setScope] = useState<RunScope>('ALL');
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [proofOfLifeMaxDays, setProofOfLifeMaxDays] = useState(180);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const run = await api.post<PayRun>(`/api/v1/payments/runs/${period}/build`, { scope, asOf, proofOfLifeMaxDays });
      onDone(run);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not build run');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Build pay run" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Period (YYYY-MM)">
          <input className="field-input" value={period} onChange={(e) => setPeriod(e.target.value)} />
        </Field>
        <Field label="Scope">
          <select className="field-input" value={scope} onChange={(e) => setScope(e.target.value as RunScope)}>
            <option value="ALL">All</option>
            <option value="STATUTORY">Statutory (S02)</option>
            <option value="WELFARE">Welfare (S03)</option>
            <option value="HOMECARE">Home care (S04)</option>
          </select>
        </Field>
        <Field label="As of date">
          <input className="field-input" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </Field>
        <Field label="Proof-of-life max age (days)">
          <input className="field-input" type="number" value={proofOfLifeMaxDays} onChange={(e) => setProofOfLifeMaxDays(Number.parseInt(e.target.value, 10))} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !period}>
            {busy ? 'Building…' : 'Build'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SendRunModal({ run, onClose, onDone }: { run: PayRun; onClose: () => void; onDone: (r: PayRun) => void }) {
  const [valueDate, setValueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [debtorAccountId, setDebtorAccountId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.post<PayRun>(`/api/v1/payments/runs/${run.id}/send`, { valueDate, debtorAccountId });
      onDone(updated);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Send ${run.period} · ${run.scope} to Treasury`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-sm text-slate-600">Total {scr(run.totals.amount)} across {run.totals.included} beneficiaries.</p>
        <Field label="Value date">
          <input className="field-input" type="date" value={valueDate} onChange={(e) => setValueDate(e.target.value)} />
        </Field>
        <Field label="Debtor (TSA sub-account) id">
          <input className="field-input" value={debtorAccountId} onChange={(e) => setDebtorAccountId(e.target.value)} placeholder="F06 bank account id" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !debtorAccountId}>
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ClawbackModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [nin, setNin] = useState('');
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/payments/clawbacks', { nin, reason, amount: Number.parseFloat(amount) });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not open clawback');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Open clawback" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
        </Field>
        <Field label="Reason">
          <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <Field label="Amount (SCR)">
          <input className="field-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !nin || !reason || !amount}>
            {busy ? 'Opening…' : 'Open'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RecoverModal({ clawback, onClose, onDone }: { clawback: Clawback; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const outstanding = Number.parseFloat(clawback.amount) - Number.parseFloat(clawback.recoveredAmount);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/payments/clawbacks/${clawback.id}/recover`, { amount: Number.parseFloat(amount) });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record recovery');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Record recovery — ${clawback.nin}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">Outstanding: {scr(outstanding)}</p>
        <Field label="Amount recovered (SCR)">
          <input className="field-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !amount}>
            {busy ? 'Saving…' : 'Record'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
